'use client'

import React, { PropsWithChildren, useEffect } from 'react'

import { createStore } from './utils/context-utils'

const getAdobeTarget = () => {
  if (typeof window === 'undefined') {
    return
  }

  return window.adobe?.target
}

const getOffers = async () => {
  const adobeTarget = getAdobeTarget()

  if (adobeTarget?.getOffers == null) {
    return
  }

  return await adobeTarget
    .getOffers({
      request: {
        execute: {
          mboxes: [
            {
              index: 0,
              name: 'homepage'
            }
          ]
        }
      }
    })
    .then(
      (response) =>
        response as {
          execute?: {
            mboxes?: Array<{
              name?: string
              index?: number
              options?: Array<{
                type?: string
                content?: {
                  customerAudience?: string
                }
              }>
            }>
          }
        }
    )
}

const { StoreProvider, useStore } = createStore({
  defaultStore: { isLoading: true, userAudiences: [] as Array<string> },
  actions: {
    setIsLoading: (slice, value: boolean) => {
      slice.isLoading = value
    },
    setUserAudiences: (slice, userAudiences: Array<string>) => {
      slice.userAudiences = userAudiences
    }
  }
})

const AdobeTargetInit: React.FC = () => {
  const { actions } = useStore()

  useEffect(() => {
    const handleLibraryLoaded = () => {
      actions.setIsLoading(true)

      getOffers()
        .then((response) => {
          const userAudiences = (response?.execute?.mboxes ?? []).flatMap((mbox) =>
            (mbox?.options ?? [])
              .map((option) => option.content?.customerAudience)
              .filter((customerAudience) => customerAudience != null)
          )

          actions.setUserAudiences(userAudiences)
        })
        .catch(() => {
          // TODO handle error
          // eslint-disable-next-line no-console
          console.log('Failed to fetch Adobe Target')
        })
        .finally(() => {
          actions.setIsLoading(false)
        })
    }

    // Fires when the Adobe Target at.js library has fully loaded
    // https://experienceleague.adobe.com/en/docs/target-dev/developer/client-side/at-js-implementation/functions-overview/atjs-custom-events
    document.addEventListener('at-library-loaded', handleLibraryLoaded)

    return () => {
      document.removeEventListener('at-library-loaded', handleLibraryLoaded)
    }
  }, [])

  return null
}

export const AdobeTarget: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <StoreProvider>
      <AdobeTargetInit />
      {children}
    </StoreProvider>
  )
}

export const useAdobeTargetContext = useStore
